import {O} from "web-vcore";

/** Renderer-thread singleton with wall-clock timings for the terrain pipeline's two main
 * phases (updated by `TerrainRenderer.tsx`, read by the map-UI Stats panel; mirrors
 * `mapRenderStats`). Solve round-trip: `lastSolveMs`/`lastSolveDispatchedAt`; camera-fill eval: `lastEvalFillMs`/`lastEvalFillAt`. */
class TerrainProcessStatsStore {
	/** Wall-clock (ms) of the most recent `solveTerrain` round-trip, or `null` until the
	 *  first solve completes. */
	@O accessor lastSolveMs: number | null = null;

	/** `performance.now()` at the `solveTerrain` dispatch moment, or `null` when no solve
	 *  is in flight.  Used by the Stats panel to show a live "in flight" duration while the
	 *  solve is pending.  Cleared to `null` on the `solveTerrainDone` reply. */
	@O accessor lastSolveDispatchedAt: number | null = null;

	/** Wall-clock (ms) of the most recent full camera-fill eval phase (first eval batch
	 *  after a solve settles → quadtree eval queue drained), or `null` until the first
	 *  full fill completes. */
	@O accessor lastEvalFillMs: number | null = null;

	/** `performance.now()` marking when the current (in-flight) eval-fill window started,
	 *  or `null` when no eval-fill is in progress. */
	@O accessor lastEvalFillAt: number | null = null;

	/** Set15 P3 diagnostic — the REQUESTED fine/near-field cell size (m) driving the working-grid
	 *  solve (2D: `mode2D_solverFineCell_sizeAsPx / worldToScreen`; 3D: `mode3D_fineCellSize`), written
	 *  each frame by `TerrainRenderer`.  This is what the solver was ASKED for — NOT necessarily the
	 *  realized size (see `fineCellSize_final`).  Lets the Stats panel compare the 2D vs 3D solver
	 *  request — a large value in 3D means the terrain is being requested too coarsely there. */
	@O accessor fineCellSize_requested: number | null = null;

	/** Set15 P5 diagnostic — the FINAL/ACTUAL solved near-field cell size (m), reported by the
	 *  worker on each `prepareTerrain`/`solveTerrain` reply (`WorkingGridResult.effectiveCellSize`
	 *  of `cache.working`).  Unlike `fineCellSize_requested` (the renderer's *requested* solve
	 *  spacing), this is the real dyadic-snapped resolution after the leaf-budget coarsening.  Covers BOTH
	 *  routes: ring stack ON → `working = rings[0]`, so it's the ring-0 realized near-field cell;
	 *  ring stack OFF → the budget-adjusted working-grid cell.  `null` until the first solve reply. */
	@O accessor fineCellSize_final: number | null = null;

	/** Set15 P3 diagnostic — whether the current render is perspective (3D) or ortho (2D),
	 *  for attributing the `fineCellSize_requested` readout. */
	@O accessor isPerspective: boolean | null = null;

	/** Set15 P5 debug — the renderer's per-worker readiness gate (`readyGenByWorkerRef[i] >= cameraGen`).
	 *  A worker stuck below the current `cameraGen` never receives `evalChunkBatch` → the terrain never
	 *  evaluates (permanent skybox).  Exposed so the agent can see WHICH worker is blocking the gate. */
	@O accessor workerReadyGens: number[] | null = null;
	/** Set15 P5 debug — the renderer's current `cameraGen` (monotonic per camera move). */
	@O accessor cameraGen: number | null = null;
	/** Set15 P5 debug — cumulative `evalChunkBatch` dispatches sent by the renderer.  Static while a worker
	 *  has `evalChunkBatchesReceived` incrementing ⇒ messages are in flight; static with no worker activity ⇒
	 *  the eval gate (`workerReadyGens`) is blocking dispatch. */
	@O accessor evalDispatchesSent: number = 0;
	/** Set15 P5 debug — cumulative `evalChunkBatch` replies applied by the renderer.  Must track
	 *  `evalDispatchesSent` eventually; a persistent gap = in-flight batches (or a worker reply stall). */
	@O accessor evalRepliesApplied: number = 0;

	/** Set15.5 P6 diagnostics — the LIVE eval-queue length (`qt.evalQueueLength`) and in-flight
	 *  eval-batch count (`evalInFlightRef`), surfaced each loop tick by the renderer so the Stats
	 *  panel can show WHY the "Eval/Sample" window is still open: `evalQueueLength`>0 (leaves still
	 *  queued) vs `evalInFlight`>0 (a dispatch awaiting a worker reply).  `evalQueueLength`/`evalInFlight`
	 *  are `null` until the first tick writes them. */
	@O accessor evalQueueLength: number | null = null;
	@O accessor evalInFlight: number | null = null;
	/** Set15.5 P6 — whether the quadtree is in 3D halt mode with the LRU at the hard ceiling (see
	 *  `TerrainQuadtree.evalHaltedAtCeiling`).  When true, the residual far-field eval queue is the
	 *  intentionally-unrendered far field, NOT work still in progress — `checkEvalFillDrained`
	 *  treats it as complete. */
	@O accessor evalHaltedAtCeiling: boolean | null = null;

	// ── Set15.5 P5 (§7) — RENDER-LATTICE diagnostics ──────────────────────────────────────────
	// The whole reason Set15.5 exists: the render lattice was 64× too coarse while every SOLVE stat
	// read healthy.  These surface the RENDER side so a lattice↔solve mismatch is observable.

	/** The REALIZED render-lattice spacing (metres) at the view centre (= leaf width / 32, since
	 *  CHUNK_N − 1 = 32 intervals across 33 samples, §4.1).  The number that was actually wrong in
	 *  §1.1.  DIRECTLY comparable to `fineCellSize_final`: a large ratio between them IS the bug.
	 *  `null` until the quadtree has a leaf under the eye. */
	@O accessor renderLatticeSpacing_atViewCentre: number | null = null;

	/** The number of leaves actually VISIBLE this frame (their bounds intersect the frustum-culled
	 *  `viewportBounds`).  Makes P1's cull gain observable. `null` until the first tree build. */
	@O accessor visibleLeafCount: number | null = null;

	/** The RAW chunk DEMAND the LOD metric wants (P3's `update()` demand formula, unclamped — the
	 *  count that would exist if `MAX_CHUNKS_HARD_CEILING` did not bind).  Budget headroom =
	 *  `MAX_CHUNKS_HARD_CEILING - demandedLeafCount`.  `null` until the first `update()`. */
	@O accessor demandedLeafCount: number | null = null;

	/** The LOD-coarsening factor (P3 §5.3), ≥ 1.  Non-1.0 ⇒ the budget is binding and quality is
	 *  being traded — otherwise invisible.  1.0 = no coarsening.  Ortho stays 1.0 (P3 gates it out
	 *  of 2D — R2). */
	@O accessor lodCoarseningFactor: number | null = null;

	/** The number of LIVE chunks in the LRU (evaluated/being-evaluated leaves) this frame.  The
	 *  churn canary vs `MAX_CHUNKS_HARD_CEILING`: if `chunkCount` stays pinned at the ceiling while
	 *  `lodCoarseningFactor` is still > 1, the tree is saturated (cf. the Set15 P5 `cameraGen`
	 *  canary). */
	@O accessor chunkCount: number | null = null;

	/** Set15.5 P6 — whether the current 3D view hit the hard chunk ceiling and the renderer HALTED
	 *  eval (far chunks uneval'd).  `true` ⇒ the Stats panel shows a red warning: the viewpoint /
	 *  map-viewer settings demand more chunks than `MAX_CHUNKS_HARD_CEILING` allows, so the far
	 *  field is not rendered.  Only meaningful under perspective; ortho (2D) is always `null`. */
	@O accessor renderLimitHit: boolean | null = null;

	/** Set9 follow-up — the number of DISTINCT per-set shader programs currently assembled for the
	 *  terrain (the `TerrainMaterialManager.programs` cache size).  Written each render body by the
	 *  renderer.  This is the "how many shaders does this map generate" diagnostic: structurally-
	 *  identical recipe sets collapse onto one program (D1), so the count is the number of genuinely
	 *  distinct structural-hash sets + the base-less fast-path program.  `null` until the shader
	 *  path has assembled at least one program. */
	@O accessor terrainProgramCount: number | null = null;

	// ── Set13 — async-shader-compile progress (the "Compiling shaders (X/Y)" label) ──────────
	/** Set13 — the number of three-layer material compiles (`compileAsync`) still awaiting driver
	 *  completion THIS wave.  `null` = nothing compiling (the MenuBar label is hidden).  Written
	 *  by `TerrainMaterialManager` (noteCompileIssued/noteCompileResolved). */
	@O accessor materialCompilesPending: number | null = null;
	/** Set13 — the total number of compiles issued in the current wave (the Y in "X/Y").  Reset
	 *  to 0 at the start of each new wave (when pending returns from null/0). */
	@O accessor materialCompilesTotal: number = 0;
}

/** Singleton observable store read by the map-UI Stats panel "Terrain Info" section. */
export const terrainProcessStats = new TerrainProcessStatsStore();
